import Clipboard from 'clipboard';
import type { Directive, DirectiveBinding } from 'vue';
import lang from '~/core/lang';

function createClipboard(el: any, binding: DirectiveBinding) {
    if (binding.value === null) return;
    if (!el.classList.contains('cursor-pointer')) el.classList.add('cursor-pointer');

    const clipboard = new Clipboard(el, {
        // innerText, not innerHTML: markup would copy HTML entities. The
        // fake textarea must live inside the element, not document.body,
        // or a modal's focus trap steals focus before the copy executes.
        text: () => binding.value ?? el.innerText,
        action: () => 'copy',
        container: el,
    });

    clipboard.on('success', () => {
        if (el._tooltip_destroy_timeout) {
            clearTimeout(el._tooltip_destroy_timeout);
        }

        const floating = document.createElement('div');
        floating.className = 'floating-tooltip';
        floating.textContent = lang.global.t('generic.copied_to_clipboard');
        floating.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            background: var(--color-primary-600);
            color: var(--color-primary-50);
            padding: 8px 12px;
            border-radius: var(--radius);
            font-size: var(--text-sm);
            line-height: 1.4;
            z-index: 9999;
            max-width: 300px;
            word-wrap: break-word;
            opacity: 1;
            pointer-events: none;
            border: 1px solid color-mix(in srgb, var(--color-primary-400) 20%, transparent);
        `;

        document.body.appendChild(floating);

        void import('@floating-ui/dom').then(({ computePosition, flip, offset, shift }) => {
            computePosition(el, floating, {
                placement: 'bottom-start',
                middleware: [offset(8), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
                floating.style.left = `${x}px`;
                floating.style.top = `${y}px`;
            });
        });

        el._floating_tooltip = floating;

        el._tooltip_destroy_timeout = setTimeout(() => {
            if (el._floating_tooltip && el._floating_tooltip.parentNode) {
                el._floating_tooltip.parentNode.removeChild(el._floating_tooltip);
                delete el._floating_tooltip;
            }
        }, 2000);
    });

    // TODO: if it is a text field and copying fails, the user won't be able to really copy the value :/
    clipboard.on('error', (e) => alert(`Failed to copy ${e.text} to clipboard! ${e}`));

    el._clipboard = clipboard;
}

const directive: Directive = {
    mounted: (el, binding) => {
        createClipboard(el, binding);
    },
    updated: (el, binding) => {
        el._clipboard?.destroy();
        delete el._clipboard;

        createClipboard(el, binding);
    },
    unmounted: (el) => {
        el._clipboard?.destroy?.();
        delete el._clipboard;
    },
};

export default directive;
